import React, { FC, useMemo, useEffect } from 'react';
import { ApolloError, useLazyQuery, useQuery } from '@apollo/client';
import { Box, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { ConnectorCard } from 'src/components/ConnectorCard';
import { McpConnectorSection } from 'src/components/McpConnectorSection';
import { ROUTE_SOURCES } from 'src/constants/routeSources';
import { ReactComponent as FacebookAdsIcon } from 'src/icons/facebook.svg';
import { ReactComponent as GoogleAdsIcon } from 'src/icons/googleAds.svg';
import { ReactComponent as GoogleAnalyticsIcon } from 'src/icons/googleAnalytics.svg';
import { ReactComponent as GoogleSearchConsoleIcon } from 'src/icons/googleSearchConsole.svg';
import { ReactComponent as ShopifyIcon } from 'src/icons/shopify.svg';
import { ReactComponent as MagentoIcon } from 'src/icons/magento.svg';
import {
  ConnectorsContainer,
  SubTitle,
  Title,
  Wrapper,
} from 'src/pages/Connectors/styled';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { useAccount } from 'src/providers/account/hook/useAccount';
import {
  GET_CONNECTORS,
  GET_FACEBOOK_AUTH_URL,
  GET_GOOGLE_ADS_AUTH_URL,
  GET_GOOGLE_ANALYTICS_AUTH_URL,
  GET_GOOGLE_SEARCH_CONSOLE_AUTH_URL,
  GET_SHOPIFY_AUTH_URL,
} from 'src/utils/gql/queries';
import {
  ConnectorType,
  Query,
  QueryGetConnectorsArgs,
  QueryGetShopifyAuthUrlArgs,
} from 'src/utils/gql/types';

export const Connectors: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const snackbar = useSnackbar();
  const { user } = useAccount();

  const onError = (err: ApolloError) => {
    const { graphQLErrors, message: errorText } = err;
    const message =
      graphQLErrors && graphQLErrors.length > 0
        ? graphQLErrors[0].message
        : errorText;

    snackbar(message);
  };

  const [
    getShopifyAuthUrl,
    {
      loading: gettingShopifyAuthUrl,
      data: shopifyAuthData,
      error: shopifyAuthError,
    },
  ] = useLazyQuery<
    Pick<Query, 'getShopifyAuthUrl'>,
    QueryGetShopifyAuthUrlArgs
  >(GET_SHOPIFY_AUTH_URL, {
    variables: { shop: '' },
  });

  const handleShopifyConnect = (type: ConnectorType) => {
    const params = new URLSearchParams(location.search);
    const shopFromUrl = params.get('shop');
    const storedShop = sessionStorage.getItem('shopify_shop');
    const shop = (shopFromUrl || storedShop || '').trim();

    if (!shop) {
      snackbar(
        'Missing Shopify shop parameter. Open Connectors from a Shopify install link, or connect via the Shopify app.',
      );
      return;
    }

    getShopifyAuthUrl({ variables: { shop } });
  };

  const handleFacebookConnect = (type: ConnectorType) => {
    getFacebookAuthUrl();
  };

  const handleGoogleAdsConnect = (type: ConnectorType) => {
    getGoogleAdsAuthUrl();
  };

  const handleGoogleAnalyticsConnect = (type: ConnectorType) => {
    getGoogleAnalyticsAuthUrl();
  };

  const handleGoogleSearchConsoleConnect = (_type: ConnectorType) => {
    getGoogleSearchConsoleAuthUrl();
  };

  const [
    getFacebookAuthUrl,
    {
      loading: gettingFacebookAuthUrl,
      data: facebookAuthData,
      error: facebookAuthError,
    },
  ] = useLazyQuery<Pick<Query, 'getFacebookAuthUrl'>, never>(
    GET_FACEBOOK_AUTH_URL,
  );

  const [
    getGoogleAdsAuthUrl,
    {
      loading: gettingGoogleAdsAuthUrl,
      data: googleAdsAuthData,
      error: googleAdsAuthError,
    },
  ] = useLazyQuery<Pick<Query, 'getGoogleAdsAuthUrl'>, never>(
    GET_GOOGLE_ADS_AUTH_URL,
  );

  const [
    getGoogleAnalyticsAuthUrl,
    {
      loading: gettingGoogleAnalyticsAuthUrl,
      data: analyticsAuthData,
      error: analyticsAuthError,
    },
  ] = useLazyQuery<Pick<Query, 'getAnalyticsAuthUrl'>, never>(
    GET_GOOGLE_ANALYTICS_AUTH_URL,
  );

  const [
    getGoogleSearchConsoleAuthUrl,
    {
      loading: gettingGoogleSearchConsoleAuthUrl,
      data: searchConsoleAuthData,
      error: searchConsoleAuthError,
    },
  ] = useLazyQuery<Pick<Query, 'getGoogleSearchConsoleAuthUrl'>, never>(
    GET_GOOGLE_SEARCH_CONSOLE_AUTH_URL,
  );

  const {
    data: connectorsData,
    loading: connectorsLoading,
    error: connectorsError,
  } = useQuery<Pick<Query, 'getConnectors'>, QueryGetConnectorsArgs>(
    GET_CONNECTORS,
    { fetchPolicy: 'network-only' },
  );

  const facebookAccountsNumber = useMemo(
    () =>
      connectorsData?.getConnectors.filter(
        (account) => account.connectorType === ConnectorType.FACEBOOK,
      ).length,
    [connectorsData?.getConnectors],
  );
  const shopifyAccountsNumber = useMemo(
    () =>
      connectorsData?.getConnectors.filter(
        (account) => account.connectorType === ConnectorType.SHOPIFY,
      ).length,
    [connectorsData?.getConnectors],
  );
  const googleAdsAccountsNumber = useMemo(
    () =>
      connectorsData?.getConnectors.filter(
        (account) => account.connectorType === ConnectorType.GOOGLE_ADS,
      ).length,
    [connectorsData?.getConnectors],
  );
  const googleAnalyticsAccountsNumber = useMemo(
    () =>
      connectorsData?.getConnectors.filter(
        (account) => account.connectorType === ConnectorType.GOOGlE_ANALYTICS4,
      ).length,
    [connectorsData?.getConnectors],
  );
  const googleSearchConsoleAccountsNumber = useMemo(
    () =>
      connectorsData?.getConnectors.filter(
        (account) =>
          account.connectorType === ConnectorType.GOOGLE_SEARCH_CONSOLE,
      ).length,
    [connectorsData?.getConnectors],
  );
  const databaseAccountsNumber = useMemo(
    () =>
      connectorsData?.getConnectors.filter(
        (account) => account.connectorType === ConnectorType.DATABASE,
      ).length,
    [connectorsData?.getConnectors],
  );
  const magentoAccountsNumber = useMemo(
    () =>
      connectorsData?.getConnectors.filter(
        (account) => account.connectorType === ConnectorType.MAGENTO,
      ).length,
    [connectorsData?.getConnectors],
  );

  const handleConfigure = (type: ConnectorType) => {
    navigate(`${ROUTE_SOURCES.ACCOUNTS}?type=${type}`);
  };

  const handleView = (type: ConnectorType) => {
    if (type === ConnectorType.DATABASE) {
      navigate('/database-connector');
    } else if (type === ConnectorType.MAGENTO) {
      navigate('/magento-connector');
    } else {
      navigate(`${ROUTE_SOURCES.METRICS}?type=${type}`);
    }
  };

  // Handle Shopify auth URL redirect
  useEffect(() => {
    const url = shopifyAuthData?.getShopifyAuthUrl;
    if (url) {
      window.location.href = url;
    }
  }, [shopifyAuthData]);

  // Handle Facebook auth URL redirect
  useEffect(() => {
    if (facebookAuthData?.getFacebookAuthUrl) {
      window.location.href = facebookAuthData.getFacebookAuthUrl;
    }
  }, [facebookAuthData]);

  // Handle Google Ads auth URL redirect
  useEffect(() => {
    if (googleAdsAuthData?.getGoogleAdsAuthUrl) {
      window.location.href = googleAdsAuthData.getGoogleAdsAuthUrl;
    }
  }, [googleAdsAuthData]);

  // Handle Analytics auth URL redirect
  useEffect(() => {
    if (analyticsAuthData?.getAnalyticsAuthUrl) {
      window.location.href = analyticsAuthData.getAnalyticsAuthUrl;
    }
  }, [analyticsAuthData]);

  // Handle Search Console auth URL redirect
  useEffect(() => {
    if (searchConsoleAuthData?.getGoogleSearchConsoleAuthUrl) {
      window.location.href =
        searchConsoleAuthData.getGoogleSearchConsoleAuthUrl;
    }
  }, [searchConsoleAuthData]);

  // Handle Shopify auth error
  useEffect(() => {
    if (shopifyAuthError) {
      onError(shopifyAuthError);
    }
  }, [shopifyAuthError, onError]);

  // Handle Facebook auth error
  useEffect(() => {
    if (facebookAuthError) {
      onError(facebookAuthError);
    }
  }, [facebookAuthError, onError]);

  // Handle Google Ads auth error
  useEffect(() => {
    if (googleAdsAuthError) {
      onError(googleAdsAuthError);
    }
  }, [googleAdsAuthError, onError]);

  // Handle Analytics auth error
  useEffect(() => {
    if (analyticsAuthError) {
      onError(analyticsAuthError);
    }
  }, [analyticsAuthError, onError]);

  // Handle Search Console auth error
  useEffect(() => {
    if (searchConsoleAuthError) {
      onError(searchConsoleAuthError);
    }
  }, [searchConsoleAuthError, onError]);

  // Handle connectors error
  useEffect(() => {
    if (connectorsError) {
      onError(connectorsError);
    }
  }, [connectorsError, onError]);

  return (
    <Wrapper>
      <Title variant="h3">Connectors</Title>
      <ConnectorsContainer>
        <Box>
          <SubTitle>E-commerce</SubTitle>
          <ConnectorCard
            type={ConnectorType.SHOPIFY}
            accountsNumber={shopifyAccountsNumber || 0}
            icon={<ShopifyIcon />}
            title="Shopify"
            description="E-commerce platform"
            buttonLoading={gettingShopifyAuthUrl}
            loading={connectorsLoading}
            onConnect={handleShopifyConnect}
            onConfigure={handleConfigure}
            onView={handleView}
          />
          {user?.magentoConnectorEnabled && (
          <Box sx={{ mt: 2 }}>
            <ConnectorCard
              type={ConnectorType.MAGENTO}
              accountsNumber={magentoAccountsNumber || 0}
              icon={<MagentoIcon />}
              title="Magento"
              description="Adobe Commerce / Magento 2"
              buttonLoading={false}
              loading={connectorsLoading}
              onConnect={() => navigate('/magento-connector')}
              onConfigure={handleConfigure}
              onView={handleView}
            />
          </Box>
          )}
        </Box>

        <Box>
          <SubTitle>Analytics</SubTitle>
          <ConnectorCard
            type={ConnectorType.GOOGlE_ANALYTICS4}
            accountsNumber={googleAnalyticsAccountsNumber || 0}
            icon={<GoogleAnalyticsIcon />}
            title="Google Analytics 4"
            description="Analytics platform"
            buttonLoading={gettingGoogleAnalyticsAuthUrl}
            loading={connectorsLoading}
            onConnect={handleGoogleAnalyticsConnect}
            onConfigure={handleConfigure}
            onView={handleView}
          />
          <Box sx={{ mt: 2 }}>
            <ConnectorCard
              type={ConnectorType.GOOGLE_SEARCH_CONSOLE}
              accountsNumber={googleSearchConsoleAccountsNumber || 0}
              icon={<GoogleSearchConsoleIcon />}
              title="Google Search Console"
              description="Organic search performance"
              buttonLoading={gettingGoogleSearchConsoleAuthUrl}
              loading={connectorsLoading}
              onConnect={handleGoogleSearchConsoleConnect}
              onConfigure={handleConfigure}
              onView={handleView}
            />
          </Box>
        </Box>

        <Box>
          <SubTitle>Advertising</SubTitle>
          <ConnectorCard
            type={ConnectorType.GOOGLE_ADS}
            accountsNumber={googleAdsAccountsNumber || 0}
            icon={<GoogleAdsIcon />}
            title="Google Ads"
            description="Advertising platform"
            buttonLoading={gettingGoogleAdsAuthUrl}
            loading={connectorsLoading}
            onConnect={handleGoogleAdsConnect}
            onConfigure={handleConfigure}
            onView={handleView}
          />
        </Box>

        <ConnectorCard
          type={ConnectorType.FACEBOOK}
          accountsNumber={facebookAccountsNumber || 0}
          icon={<FacebookAdsIcon />}
          title="Facebook Ads"
          description="Advertising platform"
          buttonLoading={gettingFacebookAuthUrl}
          loading={connectorsLoading}
          onConnect={handleFacebookConnect}
          onConfigure={handleConfigure}
          onView={handleView}
        />

        {user?.databaseConnectorEnabled && (
          <Box>
            <SubTitle>Database</SubTitle>
            <ConnectorCard
              type={ConnectorType.DATABASE}
              accountsNumber={databaseAccountsNumber || 0}
              icon={<div>🗄️</div>}
              title="Database Connector"
              description="Connect external databases"
              buttonLoading={false}
              loading={connectorsLoading}
              onConnect={() => navigate('/database-connector')}
              onConfigure={handleConfigure}
              onView={handleView}
            />
          </Box>
        )}

        <McpConnectorSection />
      </ConnectorsContainer>
    </Wrapper>
  );
};
